import React from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { signOut } from "firebase/auth";
import { auth } from "../services/firebase";

/**
 * The whole app used to disappear when any component threw.
 *
 * React unmounts the entire tree on an uncaught render error, so a single bad
 * field on one record took the nav and the logout button with it: a white page
 * the user could not even sign out of, which is exactly how it was reported
 * from the field. There was no boundary anywhere in this codebase.
 *
 * This is deliberately placed above the Router rather than per-page: a crash in
 * Layout, in AuthProvider's consumers, or in routing itself has to be caught
 * too, and those are the ones that leave no escape.
 *
 * Sign out is the important button. A crash caused by the signed-in user's own
 * data repeats on every reload, so "try again" alone would trap them in it —
 * signing out drops the session that is feeding the bad render. It calls
 * firebase/auth directly rather than through AuthContext because a class
 * component cannot read a hook-based context, and because the context provider
 * is one of the things that may have just died.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack in the console: this is the only record of what happened,
    // and support asks users for exactly this.
    console.error("Dashboard crashed:", error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Sign-out from the error screen failed:", e);
    }
    // Full navigation, not a router push: the router may be part of what broke.
    window.location.assign(`${process.env.REACT_APP_ROUTER_BASENAME || "/"}login`.replace("//", "/"));
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        px={3}
        textAlign="center"
      >
        <Typography variant="h5">Something went wrong</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
          This page hit an error and stopped. Reloading often clears it. If it
          keeps happening, sign out and back in, and send support the message
          below.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          component="pre"
          sx={{ maxWidth: "100%", overflowX: "auto", whiteSpace: "pre-wrap" }}
        >
          {String(this.state.error?.message || this.state.error)}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={this.handleReload}>
            Reload
          </Button>
          <Button variant="outlined" color="error" onClick={this.handleSignOut}>
            Sign out
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
